import { useRef, useCallback } from "react";

interface LongPressOptions {
	delay?: number;
	moveThreshold?: number;
	feedbackDelay?: number;
}

interface LongPressHandlers {
	onTouchStart: (e: React.TouchEvent) => void;
	onTouchMove: (e: React.TouchEvent) => void;
	onTouchEnd: () => void;
	onTouchCancel: () => void;
	onContextMenu: (e: React.SyntheticEvent) => void;
	isPressing: React.RefObject<boolean>;
}

export function useLongPress(
	onLongPress: () => void,
	{
		delay = 500,
		moveThreshold = 10,
		feedbackDelay = 200,
	}: LongPressOptions = {},
): LongPressHandlers {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const startX = useRef(0);
	const startY = useRef(0);
	const fired = useRef(false);
	const isPressing = useRef(false);
	const targetRef = useRef<HTMLElement | null>(null);

	const restoreUserSelect = useCallback(() => {
		if (targetRef.current) {
			targetRef.current.style.userSelect = "";
			targetRef.current = null;
		}
	}, []);

	const clear = useCallback(() => {
		if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
		if (feedbackTimerRef.current) { clearTimeout(feedbackTimerRef.current); feedbackTimerRef.current = null; }
		isPressing.current = false;
	}, []);

	const onTouchStart = useCallback((e: React.TouchEvent) => {
		const touch = e.touches[0];
		startX.current = touch.clientX;
		startY.current = touch.clientY;
		fired.current = false;

		// Suppress browser text selection during long-press detection window
		targetRef.current = e.currentTarget as HTMLElement;
		targetRef.current.style.userSelect = "none";

		// Start visual feedback after feedbackDelay
		feedbackTimerRef.current = setTimeout(() => {
			isPressing.current = true;
		}, feedbackDelay);

		// Fire long-press after full delay
		timerRef.current = setTimeout(() => {
			fired.current = true;
			isPressing.current = false;
			// Clear any partial text selection the browser may have started
			try { window.getSelection()?.removeAllRanges(); } catch {}
			// Haptic feedback
			try { navigator.vibrate?.(50); } catch {}
			onLongPress();
		}, delay);
	}, [onLongPress, delay, feedbackDelay]);

	const onTouchMove = useCallback((e: React.TouchEvent) => {
		const touch = e.touches[0];
		const dx = Math.abs(touch.clientX - startX.current);
		const dy = Math.abs(touch.clientY - startY.current);
		if (dx > moveThreshold || dy > moveThreshold) {
			clear();
			// Restore user-select so the browser can attempt text selection on drag
			// (works on Android; iOS does not resume text selection mid-gesture)
			if (!fired.current) {
				restoreUserSelect();
			}
		}
	}, [moveThreshold, clear, restoreUserSelect]);

	const onTouchEnd = useCallback(() => {
		clear();
		// Always restore after touch ends — selectionMode CSS class takes over if needed
		restoreUserSelect();
	}, [clear, restoreUserSelect]);

	const onTouchCancel = useCallback(() => {
		clear();
		restoreUserSelect();
	}, [clear, restoreUserSelect]);

	const onContextMenu = useCallback((e: React.SyntheticEvent) => {
		// Prevent native context menu during long-press
		if (fired.current || timerRef.current) {
			e.preventDefault();
		}
	}, []);

	return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel, onContextMenu, isPressing };
}
